import { useEffect, useState } from 'react'
import { Loader2, Filter, X, ChevronLeft, ChevronRight, Activity, Calendar, User } from 'lucide-react'
import Toast from '../components/Toast'
import { activityLogsApi, type ActivityLog, type ActivityLogFilters } from '../services/activityLogsApi'
import { useAuthStore } from '../stores/authStore'
import { useTenantsStore } from '../stores/tenantsStore'

type ToastState = {
    message: string
    type: 'success' | 'error'
} | null

const ActivityLogs = () => {
    const { getUserRole } = useAuthStore()
    const userRole = getUserRole()

    const tenants = useTenantsStore(state => state.tenants)
    const fetchTenants = useTenantsStore(state => state.fetchTenants)

    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [toast, setToast] = useState<ToastState>(null)
    const [showFilters, setShowFilters] = useState(false)
    const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null)

    const [filters, setFilters] = useState<ActivityLogFilters>({
        email: '',
        tenantId: '',
        actionType: '',
        entityType: '',
        entityId: '',
        startDate: '',
        endDate: '',
    })

    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 1,
    })

    const fetchActivityLogs = async () => {
        if (userRole !== 'ADMIN') return

        setLoading(true)
        setError(null)
        try {
            const params: ActivityLogFilters = {
                page: pagination.page,
                limit: pagination.limit,
            }

            // Add filters if they are set
            if (filters.email) params.email = filters.email
            if (filters.tenantId) params.tenantId = filters.tenantId
            if (filters.actionType) params.actionType = filters.actionType
            if (filters.entityType) params.entityType = filters.entityType
            if (filters.entityId) params.entityId = filters.entityId
            if (filters.startDate) params.startDate = filters.startDate
            if (filters.endDate) params.endDate = filters.endDate

            const response = await activityLogsApi.getActivityLogs(params)

            if (response.success && response.data) {
                // Handle different possible response structures
                let logs: ActivityLog[] = []
                let paginationData: any = {}

                // Check if data is directly an array (some APIs do this)
                if (Array.isArray(response.data)) {
                    logs = response.data
                    paginationData = (response as any).pagination || {}
                } else {
                    // Otherwise, assume nested structure: response.data.data
                    const apiData = response.data as any
                    logs = apiData.data || []
                    paginationData = apiData.pagination || {}
                }

                setActivityLogs(logs)
                setPagination({
                    total: paginationData.total || 0,
                    page: paginationData.page || 1,
                    limit: paginationData.limit || 20,
                    totalPages: paginationData.totalPages || 1,
                })
            } else {
                setError('Failed to fetch activity logs')
            }
        } catch (err) {
            setError('An error occurred while fetching activity logs')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (userRole === 'ADMIN') {
            fetchActivityLogs()
        }
    }, [userRole, pagination.page, pagination.limit])

    useEffect(() => {
        if (userRole === 'ADMIN') {
            fetchTenants()
        }
    }, [userRole, fetchTenants])

    useEffect(() => {
        if (error) {
            setToast({ message: error, type: 'error' })
        }
    }, [error])

    const handleFilterChange = (key: keyof ActivityLogFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }))
    }

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }))
        fetchActivityLogs()
    }

    const clearFilters = () => {
        setFilters({
            email: '',
            tenantId: '',
            actionType: '',
            entityType: '',
            entityId: '',
            startDate: '',
            endDate: '',
        })
        setPagination(prev => ({ ...prev, page: 1 }))
    }

    const closeToast = () => {
        setToast(null)
        setError(null)
    }

    const formatDate = (dateString: string) => {
        // Parse the date string as UTC and convert to local time
        const date = new Date(dateString)

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date'
        }

        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'UTC', // Display in UTC timezone
        }).format(date) + ' UTC'
    }

    const getActionColor = (actionType: string) => {
        switch (actionType.toLowerCase()) {
            case 'login':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            case 'logout':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            case 'create':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            case 'update':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
            case 'delete':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            case 'read':
                return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
            case 'status_change':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
            case 'approve':
                return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
            case 'reject':
                return 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200'
            case 'publish':
                return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
            case 'unpublish':
                return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200'
            case 'export':
                return 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200'
            case 'download':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
        }
    }

    const goToPage = (page: number) => {
        setPagination(prev => ({ ...prev, page }))
    }

    const changeLimit = (limit: number) => {
        setPagination(prev => ({ ...prev, limit, page: 1 }))
    }

    if (userRole !== 'ADMIN') {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Access restricted
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    You do not have permission to view activity logs.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Activity Logs
                    </h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Track all user activities across the system
                    </p>
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <Filter className="h-4 w-4 mr-2" />
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {showFilters && (
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                User Email
                            </label>
                            <input
                                type="text"
                                value={filters.email || ''}
                                onChange={(e) => handleFilterChange('email', e.target.value)}
                                placeholder="Enter user email"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tenant
                            </label>
                            <select
                                value={filters.tenantId || ''}
                                onChange={(e) => handleFilterChange('tenantId', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">All Tenants</option>
                                {tenants.map(tenant => (
                                    <option key={tenant._id} value={tenant._id}>
                                        {tenant.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Action Type
                            </label>
                            <input
                                type="text"
                                value={filters.actionType || ''}
                                onChange={(e) => handleFilterChange('actionType', e.target.value)}
                                placeholder="e.g., login, create, update"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Entity Type
                            </label>
                            <input
                                type="text"
                                value={filters.entityType || ''}
                                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                                placeholder="e.g., user, project, vulnerability"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Entity ID
                            </label>
                            <input
                                type="text"
                                value={filters.entityId || ''}
                                onChange={(e) => handleFilterChange('entityId', e.target.value)}
                                placeholder="Enter entity ID"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={filters.startDate || ''}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={filters.endDate || ''}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex justify-end space-x-3">
                        <button
                            onClick={clearFilters}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            <X className="h-4 w-4 mr-2" />
                            Clear Filters
                        </button>
                        <button
                            onClick={applyFilters}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            )}

            {/* Activity Timeline */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                            Activity Timeline
                        </h2>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            {pagination.total} {pagination.total === 1 ? 'activity' : 'activities'}
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                            <span className="ml-3 text-gray-500 dark:text-gray-400">Loading activities...</span>
                        </div>
                    )}

                    {!loading && activityLogs.length === 0 && (
                        <div className="text-center py-12">
                            <Activity className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No activities found</h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Try adjusting your filters or check back later.
                            </p>
                        </div>
                    )}

                    {!loading && activityLogs.length > 0 && (
                        <div className="space-y-4">
                            {activityLogs.map((log) => (
                                <div
                                    key={log._id}
                                    className="relative pl-8 pb-4 border-l-2 border-gray-200 dark:border-gray-700 last:pb-0 last:border-l-0"
                                >
                                    {/* Timeline dot */}
                                    <div className="absolute left-0 top-0 -ml-[9px] h-4 w-4 rounded-full bg-primary-500 ring-4 ring-white dark:ring-gray-800"></div>

                                    {/* Compact Card */}
                                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 hover:shadow-md transition-shadow">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.actionType)}`}>
                                                        {log.actionType.toUpperCase()}
                                                    </span>
                                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                                        {log.entityType}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <User className="h-4 w-4 text-gray-400" />
                                                        <span className="font-medium text-gray-900 dark:text-white truncate">
                                                            {log.userEmail}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                                                        <Calendar className="h-3.5 w-3.5" />
                                                        <span className="text-xs">
                                                            {formatDate(log.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setSelectedLog(log)}
                                                className="ml-4 inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loading && activityLogs.length > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-700 dark:text-gray-300">
                                    Items per page:
                                </label>
                                <select
                                    value={pagination.limit}
                                    onChange={(e) => changeLimit(Number(e.target.value))}
                                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white"
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => goToPage(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                </button>

                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Page {pagination.page} of {pagination.totalPages}
                                </span>

                                <button
                                    onClick={() => goToPage(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.totalPages}
                                    className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Activity Details Modal */}
            {selectedLog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setSelectedLog(null)}
                    />
                    <div className="relative z-10 w-full max-w-3xl bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Activity Details
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(selectedLog.actionType)}`}>
                                        {selectedLog.actionType.toUpperCase()}
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {selectedLog.entityType}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                            <div className="space-y-4">
                                {/* User Information */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                        User Information
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 space-y-2">
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                Email:
                                            </span>
                                            <span className="text-gray-900 dark:text-white font-medium">
                                                {selectedLog.userEmail}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                User ID:
                                            </span>
                                            <span className="text-gray-900 dark:text-white font-mono text-xs">
                                                {selectedLog.userId}
                                            </span>
                                        </div>
                                        {selectedLog.userRoles && selectedLog.userRoles.length > 0 && (
                                            <div className="flex items-start gap-2 text-sm">
                                                <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                    Roles:
                                                </span>
                                                <span className="text-gray-900 dark:text-white">
                                                    {selectedLog.userRoles.join(', ')}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                Timestamp:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {formatDate(selectedLog.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Service & Entity Information */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                        Service & Entity Information
                                    </h4>
                                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 space-y-2">
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                Service:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {selectedLog.serviceName}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                Module:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {selectedLog.moduleName}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                Entity Type:
                                            </span>
                                            <span className="text-gray-900 dark:text-white">
                                                {selectedLog.entityType}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2 text-sm">
                                            <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                Entity ID:
                                            </span>
                                            <span className="text-gray-900 dark:text-white font-mono text-xs break-all">
                                                {selectedLog.entityId}
                                            </span>
                                        </div>
                                        {selectedLog.tenantId && (
                                            <div className="flex items-start gap-2 text-sm">
                                                <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                    Tenant:
                                                </span>
                                                <span className="text-gray-900 dark:text-white">
                                                    {tenants.find(t => t._id === selectedLog.tenantId)?.name || selectedLog.tenantId}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Metadata */}
                                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                            Metadata
                                        </h4>
                                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 space-y-2">
                                            {Object.entries(selectedLog.metadata).map(([key, value]) => (
                                                <div key={key} className="flex items-start gap-2 text-sm">
                                                    <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                        {key}:
                                                    </span>
                                                    <span className="text-gray-900 dark:text-white break-all">
                                                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Request Metadata */}
                                {selectedLog.requestMetadata && (
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                            Request Details
                                        </h4>
                                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 space-y-2">
                                            {selectedLog.requestMetadata.method && selectedLog.requestMetadata.path && (
                                                <div className="flex items-start gap-2 text-sm">
                                                    <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                        Request:
                                                    </span>
                                                    <span className="text-gray-900 dark:text-white font-mono text-xs">
                                                        {selectedLog.requestMetadata.method} {selectedLog.requestMetadata.path}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedLog.requestMetadata.ip && (
                                                <div className="flex items-start gap-2 text-sm">
                                                    <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                        IP Address:
                                                    </span>
                                                    <span className="text-gray-900 dark:text-white font-mono text-xs">
                                                        {selectedLog.requestMetadata.ip}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedLog.requestMetadata.userAgent && (
                                                <div className="flex items-start gap-2 text-sm">
                                                    <span className="text-gray-500 dark:text-gray-400 min-w-[120px]">
                                                        User Agent:
                                                    </span>
                                                    <span className="text-gray-900 dark:text-white text-xs break-all">
                                                        {selectedLog.requestMetadata.userAgent}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={closeToast} />
            )}
        </div>
    )
}

export default ActivityLogs
